import React from 'react';
import styles from './Timeline.module.css';

const formatTime = (seconds: number) => {
  const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return `${mins}:${secs}s`;
};

interface TimelineProps {
  endTime: number; // in seconds
  step: number; // interval between main ticks (in seconds)
  pixelsPerSecond?: number; // visual scale (default: 10px/sec)
  intervals?: number; // how many equal parts to split between ticks (default: 4 = 3 dots)
}

const Timeline: React.FC<TimelineProps> = ({
  endTime,
  step,
  pixelsPerSecond = 10,
  intervals = 4, // split into 4 parts = 3 dots
}) => {
  const totalWidth = endTime * pixelsPerSecond;

  const majorTicks: number[] = [];
  for (let t = 0; t <= endTime; t += step) {
    majorTicks.push(t);
  }

  return (
    <div className={styles.timeline} style={{width: `${totalWidth}px`}}>
      {/* Main ticks */}
      {majorTicks.map((time, index) => (
        <div
          key={`tick-${index}`}
          className={styles.tick}
          style={{left: `${time * pixelsPerSecond}px`}}>
          <span className={styles.label}>{formatTime(time)}</span>
          <span className={styles.tickMark}></span>
        </div>
      ))}

      {/* Subticks (dots) between main ticks */}
      {majorTicks.map((time, index) => {
        if (index === majorTicks.length - 1) return null;

        const nextTime = majorTicks[index + 1];
        const safeIntervals = Math.max(1, intervals);
        const intervalSize = (nextTime - time) / safeIntervals;

        return Array.from({length: safeIntervals - 1}, (_, i) => {
          const subTime = time + intervalSize * (i + 1);
          return (
            <div
              key={`dot-${time}-${i}`}
              className={styles.subTick}
              style={{left: `${subTime * pixelsPerSecond}px`}}>
              &#9679;
            </div>
          );
        });
      })}
    </div>
  );
};

export default Timeline;
